import type { PickerConfig } from "./api.js";

/** The one script from outside this app: Google's own, loaded only when someone opens the file picker. */
const GOOGLE_API = "https://apis.google.com/js/api.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
declare global {
  interface Window {
    gapi?: { load: (name: string, cb: () => void) => void };
    google?: { picker?: any };
  }
}

let loading: Promise<void> | null = null;
export function loadGooglePicker(): Promise<void> {
  if (window.google?.picker) return Promise.resolve();
  loading ??= new Promise<void>((resolve, reject) => {
    const done = () => (window.gapi ? window.gapi.load("picker", () => resolve()) : reject(new Error("picker")));
    const existing = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_API}"]`);
    if (existing) return existing.addEventListener("load", done, { once: true });
    const s = document.createElement("script");
    s.src = GOOGLE_API;
    s.async = true;
    s.onload = done;
    s.onerror = () => {
      loading = null;
      reject(new Error("picker"));
    };
    document.head.appendChild(s);
  });
  return loading;
}

export interface PickedFile {
  id: string;
  name: string;
  mimeType?: string;
}

/** Opens Google's file picker with the short-lived token; resolves with the files picked (none when closed). Folders cannot be picked. */
export async function pickDriveFiles(cfg: PickerConfig, locale: string): Promise<PickedFile[]> {
  await loadGooglePicker();
  const g = window.google!.picker;
  return new Promise<PickedFile[]>((resolve) => {
    const view = new g.DocsView(g.ViewId.DOCS).setIncludeFolders(false).setSelectFolderEnabled(false);
    let b = new g.PickerBuilder()
      .addView(view)
      .enableFeature(g.Feature.MULTISELECT_ENABLED)
      .setOAuthToken(cfg.accessToken)
      .setLocale(locale)
      .setCallback((data: any) => {
        if (data[g.Response.ACTION] === g.Action.PICKED) {
          const docs = (data[g.Response.DOCUMENTS] ?? []) as any[];
          resolve(docs.filter((d) => d.mimeType !== "application/vnd.google-apps.folder").map((d) => ({ id: String(d.id), name: String(d.name ?? d.id), mimeType: d.mimeType as string | undefined })));
        } else if (data[g.Response.ACTION] === g.Action.CANCEL) resolve([]);
      });
    if (cfg.apiKey) b = b.setDeveloperKey(cfg.apiKey);
    if (cfg.appId) b = b.setAppId(cfg.appId);
    b.build().setVisible(true);
  });
}
